import { Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/styles';
import { PageSelector, useAgGridPagination } from '@tassat/ag-grid';
import { ToolbarButton } from '@tassat/button';
import { SelectOption, TransactionStatusCode, TransactionStatusCodeMap } from '@tassat/common-definitions';
import { DateRangeSelector, StyledSelect } from '@tassat/formik-input';
import { TransactionHistoryTableOptions, adminTransactionHistoryTableOptionsInitialState } from '@tassat/fred-extended-definitions';
import { Export as ExportIcon } from '@tassat/icon';
import moment from 'moment';
import React from 'react';
import { SingleValue } from 'react-select';

import Header from 'Containers/header';
import TassatPayLogo from 'Images/TassatLogoAndName.svg';
import { convertDateFilterToTime } from 'Lib/agGridHelper';
import { TRANSACTIONS_PER_EXPORT } from 'Lib/constant';
import { caseInsensitiveSort } from 'Lib/stringHelpers';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorkerWrapper';
import { haltRealtimeTransactionsAction, resumeRealtimeTransactionsAction } from 'Reducers/actions/transactionsActions';
import { useAppDispatch, useAppSelector } from 'Reducers/store/hooks';
import { getAllWalletsArray } from 'Selectors/accounts';
import { getHistoryExportAssets } from 'Selectors/assets';
import { getCurrentRole } from 'Selectors/common';
import { getFirmsArray } from 'Selectors/firms';
import { getTransactionsRealtimeHalted } from 'Selectors/transactions';
import { DateFilter } from 'SharedComponents/agGrid/filters/dateFilter/dateFilter';
import { TableOptionsContext, tableOptionsReducer } from 'SharedComponents/contexts/TableOptionsContext';
import { useExports } from 'SharedComponents/hooks/useExports';
import TransactionDetailsModal from 'SharedComponents/modals/transactionDetailsModal';
import { PageHeaderTitle } from 'SharedComponents/pageHeader';
import TransactionHistoryAgGrid from 'SharedComponents/transactions/transactionHistoryAgGrid';

import type { AdminTransactionHistoryProps } from '.';

export const PAGE_TITLE = 'Transaction History';

export function AdminTransactionHistory(props: AdminTransactionHistoryProps) {
    const { classes } = props;

    const dispatch = useAppDispatch();
    const haltRealtimeTransactions = () => dispatch(haltRealtimeTransactionsAction);
    const resumeRealtimeTransactions = () => dispatch(resumeRealtimeTransactionsAction);
    const userRole = useAppSelector(getCurrentRole);
    const realtimeHalted = useAppSelector(getTransactionsRealtimeHalted);
    const assets = useAppSelector(getHistoryExportAssets);
    const accounts = useAppSelector((state) => getAllWalletsArray(state));
    const firms = useAppSelector((state) => getFirmsArray(state, '', true));
    const theme = useTheme<ApplicationMuiTheme>();
    const [ selectedTransactionId, setSelectedTransactionId ] = React.useState<string>('');
    const [ rowCount, setRowCount ] = React.useState<number>(0);
    const [ TableOptions, TableOptionsDispatch ] = React.useReducer<(TableOptions, { type, values }: { type: string; values: object }) =>
    TransactionHistoryTableOptions>(tableOptionsReducer, {
            ...adminTransactionHistoryTableOptionsInitialState,
            DateCondition: `auditTime|GT|${moment()
                .subtract(1, 'day')
                .format('x')}`
        });
    const tableContextMemo = React.useMemo(() => {
        return {
            TableOptions,
            TableOptionsDispatch
        };
    }, [ TableOptions, TableOptionsDispatch ]);
    const [setPageNum] = useAgGridPagination(TableOptionsDispatch);
    const [ accountSelectOptions, setAccountSelectOptions ] = React.useState<SelectOption[]>([
        {
            label: 'All Accounts',
            value: 'none'
        }
    ]);
    const firmSelectOptions: SelectOption[] = React.useMemo(() => {
        firms.sort((firmA, firmB) => caseInsensitiveSort(firmA.Name, firmB.Name));

        const selectOptions = firms
            .map(firm => {
                return {
                    label: firm.Name,
                    value: firm.FirmId
                };
            });
        selectOptions.unshift({
            label: 'All Firms',
            value: 'none'
        });

        return selectOptions;
    }, [firms]);

    const {
        exportInFlight,
        setExportsContext
    } = useExports();

    React.useEffect(() => {
        if (props.location.state) {
            const firmId = props.location.state.firmId;
            const accountId = props.location.state.accountId;
            if (firmId) {
                const selectedFirm = firmSelectOptions.find(firm => firm.value === firmId);
                selectFirm(selectedFirm ?? null);
            }
            if (accountId) {
                const selectedAccount = accounts.find(account => account.AccountId === accountId);
                const firmAccounts = accounts
                    .filter(account => account.FirmId === TableOptions.SelectedFirm)
                    .sort((accountA, accountB) => caseInsensitiveSort(accountA.AccountName, accountB.AccountName))
                    .map(account => {
                        return {
                            label: account.AccountName,
                            value: account.Account
                        };
                    });
                setAccountSelectOptions(accountSelectOptions.slice(0, 1)
                    .concat(firmAccounts));
                selectAccount({
                    label: selectedAccount?.AccountName ?? '',
                    value: selectedAccount?.Account ?? ''
                });
            }
        }
    }, [ TableOptions.SelectedFirm, accounts ]);

    React.useEffect(() => {
        if (TableOptions.SelectedFirm !== 'none') {
            const firmAccounts = accounts
                .filter(account => account.FirmId === TableOptions.SelectedFirm)
                .sort((accountA, accountB) => caseInsensitiveSort(accountA.AccountName, accountB.AccountName))
                .map(account => {
                    return {
                        label: `${account.AccountName}`,
                        value: account.Account
                    };
                });
            setAccountSelectOptions(accountSelectOptions.slice(0, 1)
                .concat(firmAccounts));
        }
    }, [ TableOptions.SelectedFirm, accounts ]);

    React.useEffect(() => {
        if (!realtimeHalted && (TableOptions.FilterCondition || TableOptions.SelectedDateFilter === 'customRange')) {
            haltRealtimeTransactions();
        } else if (realtimeHalted && !TableOptions.FilterCondition && TableOptions.SelectedDateFilter !== 'customRange') {
            resumeRealtimeTransactions();
        }

        // TODO: Try to solve with some type of promise canceling in future
        // If we get filters from history state and they are not set yet
        // don't do a history request. If they are set, remove from history
        // state
        if (props.location.state) {
            if (props.location.state.firmId && TableOptions.SelectedFirm === 'none') {
                return;
            } else {
                const newState = props.location.state;
                delete newState.firmId;
                history.replaceState(newState, document.title);
            }

            if (props.location.state.accountId && TableOptions.SelectedWallet === 'none') {
                return;
            } else {
                const newState = props.location.state;
                delete newState.accountId;
                history.replaceState(newState, document.title);
            }
        }

        fetchTransactions();
    }, [
        TableOptions.DateCondition,
        TableOptions.FilterCondition,
        TableOptions.FirmCondition,
        TableOptions.WalletCondition,
        TableOptions.PageNo,
        TableOptions.Sort,
        TableOptions.SelectedDateFilter
    ]);

    React.useEffect(() => {
        if (props.location.state?.linkedRequestId) {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    ...adminTransactionHistoryTableOptionsInitialState,
                    FilterCondition: `RequestId|EQ|${props.location.state.linkedRequestId}`
                }
            });
            const lookupId = props.location.state.linkedRequestId + '-' + props.location.state.direction;
            setSelectedTransactionId(lookupId);
        }
    }, [props.location.state]);

    const selectTimeRange = (startDate: string | moment.Moment, endDate: string | moment.Moment) => {
        if (moment.isMoment(startDate) && moment.isMoment(endDate)) {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    SelectedDateFilter: 'customRange',
                    DateCondition: `auditTime|GT|${startDate.valueOf()}:auditTime|LT|${endDate.valueOf()}`,
                    PageNo: 1,
                    SelectedFilter: 'none',
                    TableFilters: [],
                    FilterCondition: ''
                }
            });
        } else if (typeof startDate === 'string') {
            TableOptionsDispatch({
                type: 'update',
                values: {
                    SelectedDateFilter: startDate,
                    DateCondition: `auditTime|GT|${convertDateFilterToTime(startDate)}`,
                    PageNo: 1,
                    SelectedFilter: 'none',
                    TableFilters: [],
                    FilterCondition: ''
                }
            });
        }
    };

    const selectFirm = (selectedFirmOption: SingleValue<SelectOption>) => {
        const selectedFirmId = selectedFirmOption?.value === 'none' ? '' : selectedFirmOption?.value;
        TableOptionsDispatch({
            type: 'update',
            values: {
                SelectedFirm: selectedFirmOption?.value,
                SelectedFirmName: selectedFirmId ? selectedFirmOption?.label : '',
                FirmCondition: selectedFirmId ? `firmId|EQ|${selectedFirmId}` : '',
                SelectedWallet: 'none',
                WalletCondition: '',
                PageNo: 1
            }
        });
    };

    const selectAccount = (selectedAccountOption: SingleValue<SelectOption>) => {
        const selectedEOAAddress = selectedAccountOption?.value === 'none' ? '' : selectedAccountOption?.value;
        TableOptionsDispatch({
            type: 'update',
            values: {
                WalletCondition: selectedEOAAddress ? `wallet|EQ|${selectedEOAAddress}` : '',
                SelectedWallet: selectedAccountOption?.value,
                PageNo: 1
            }
        });
    };

    const approvalFilter = () => {
        TableOptionsDispatch({
            type: 'update',
            values: {
                ...adminTransactionHistoryTableOptionsInitialState,
                SelectedDateFilter: '',
                DateCondition: '',
                SelectedFilter: 'approval',
                FilterCondition: `Status|EQ|${TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.value}`
            }
        });
    };

    const clientApprovalFilter = () => {
        TableOptionsDispatch({
            type: 'update',
            values: {
                ...adminTransactionHistoryTableOptionsInitialState,
                SelectedDateFilter: '',
                DateCondition: '',
                SelectedFilter: 'clientApproval',
                FilterCondition: `Status|EQ|${TransactionStatusCodeMap[TransactionStatusCode.PENDING_APPROVAL]?.value}`
            }
        });
    };

    const failedFilter = () => {
        TableOptionsDispatch({
            type: 'update',
            values: {
                ...adminTransactionHistoryTableOptionsInitialState,
                SelectedDateFilter: '',
                DateCondition: '',
                SelectedFilter: 'failed',
                FilterCondition: 'Status|IN|6'
            }
        });
    };

    const clearFilter = () => {
        TableOptionsDispatch({
            type: 'update',
            values: {
                ...adminTransactionHistoryTableOptionsInitialState,
                TableFilters: [],
                DateCondition: `auditTime|GT|${moment()
                    .subtract(1, 'day')
                    .format('x')}`
            }
        });
    };

    const fetchTransactions = async (pageNum?) => {
        const {
            PageNo,
            RecordPerPage,
            Sort,
            DateCondition,
            FirmCondition,
            FilterCondition,
            WalletCondition
        } = TableOptions;

        const res = await Worker.getInstance()
            .getTransactions({
                Condition: `${!WalletCondition && FirmCondition ? `${FirmCondition}:` : ''}${WalletCondition && `${WalletCondition}:`}${FilterCondition && `${FilterCondition}${DateCondition ? ':' : ''}`}${DateCondition}`,
                Sort,
                PageNo: pageNum || PageNo,
                RecordPerPage
            }) || { data: undefined };

        setRowCount(res.data && res.data.TotalCount);

        return res;
    };

    const fetchTransactionsExport = () => {
        const {
            Sort,
            DateCondition,
            FirmCondition,
            FilterCondition,
            WalletCondition
        } = TableOptions;

        let query = `&Condition=${!WalletCondition && FirmCondition ? `${FirmCondition}:` : ''}${WalletCondition && `${WalletCondition}:`}${FilterCondition && `${FilterCondition}${DateCondition ? ':' : ''}`}${DateCondition}`;
        query += `&Sort=${Sort}&RecordPerPage=${TRANSACTIONS_PER_EXPORT}`;

        setExportsContext((exportsContext) => ({
            ...exportsContext,
            totalChunks: Math.floor(rowCount / TRANSACTIONS_PER_EXPORT) + 1,
            chunkCounter: 0,
            exportInFlight: true,
            RecordType: 'admin-transaction',
            filterQuery: query,
            opts: { assets: assets as any }
        }));
    };

    return (
        <TableOptionsContext.Provider value={tableContextMemo}>
            <Grid
                className={classes.root}
                container
                direction='column'
                spacing={0}
                wrap='nowrap'
            >
                <Header />
                <PageHeaderTitle
                    title={PAGE_TITLE}
                />
                <Grid
                    className={classes.firmWalletSelectContainer}
                    container
                    direction='row'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        className={classes.firmSelectContainer}
                        container
                        direction='column'
                        item
                        spacing={0}
                        wrap='nowrap'
                        xs={3}
                    >
                        Firms
                        <StyledSelect
                            className={classes.firmSelect}
                            defaultValue={firmSelectOptions[0]}
                            id='transactionHistoryFirmSelect'
                            onChange={selectFirm}
                            options={firmSelectOptions}
                            value={firmSelectOptions.find(opt => opt.value === TableOptions.SelectedFirm)}
                        />
                    </Grid>
                    {TableOptions.SelectedFirm !== 'none' &&
                        <Grid
                            container
                            direction='column'
                            item
                            spacing={0}
                            wrap='nowrap'
                            xs={3}
                        >
                            Accounts
                            <StyledSelect
                                className={classes.firmSelect}
                                defaultValue={accountSelectOptions[0]}
                                onChange={selectAccount}
                                options={accountSelectOptions}
                                value={accountSelectOptions.find(opt => opt.value === TableOptions.SelectedWallet)}
                            />
                        </Grid>}
                </Grid>
                <Grid
                    container
                    direction='row'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Grid
                        alignItems='center'
                        container
                        direction='row'
                        spacing={0}
                        wrap='nowrap'
                    >
                        <DateFilter
                            selectTimeRange={(value: string) => selectTimeRange(value, '')}
                            timeFrameinDays={TableOptions.SelectedDateFilter}
                        />
                        <DateRangeSelector
                            allowSingleDay
                            className={classes.dateRangeSelectorContainer}
                            maxDate={moment()
                                .endOf('day')}
                            minDate={moment()
                                .subtract(18, 'month')
                                .startOf('day')}
                            selectTimeRange={selectTimeRange}
                            timeFrameInDays={TableOptions.SelectedDateFilter}
                        />
                        {userRole.IsTransactionHistoryAllowed &&
                            <>
                                <Typography
                                    className={`${classes.filterOption} ${TableOptions.SelectedFilter === 'approval' ? classes.selectedFilterOption : ''}`}
                                    onClick={approvalFilter}
                                    variant='h5'
                                >
                                    Approvals
                                </Typography>
                                <Typography
                                    className={`${classes.filterOption} ${TableOptions.SelectedFilter === 'clientApproval' ? classes.selectedFilterOption : ''}`}
                                    onClick={clientApprovalFilter}
                                    variant='h5'
                                >
                                    Client Approvals
                                </Typography>
                            </>}
                        {(userRole.IsTransactionHistoryAllowed || userRole.IsTransactionHistoryViewOnlyAllowed) &&
                            <Typography
                                className={`${classes.lastFilterOption} ${classes.filterOption} ${TableOptions.SelectedFilter === 'failed' ? classes.selectedFilterOption : ''}`}
                                onClick={failedFilter}
                                variant='h5'
                            >
                                Failed
                            </Typography>}
                        <Typography
                            className={classes.clearFiltersButton}
                            onClick={clearFilter}
                            variant='h5'
                        >
                            Clear Filters
                        </Typography>
                        <Grid
                            alignItems='center'
                            className={classes.exportContainer}
                            container
                            direction='row'
                            justifyContent='flex-end'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.realtimeTag}
                                variant='h5'
                            >
                                Real Time Updates
                                <span className={`${classes.realtimeState} ${realtimeHalted ? classes.realtimeDisabled : classes.realtimeEnabled}`}>{realtimeHalted ? 'OFF' : 'ON'}</span>
                            </Typography>
                            <ToolbarButton
                                actionInProgress={exportInFlight}
                                Icon={ExportIcon}
                                iconClassName={classes.exportIcon}
                                onClick={fetchTransactionsExport}
                                text='Export'
                            />
                        </Grid>
                    </Grid>
                </Grid>
                <Grid
                    className={classes.transactionGridContainer}
                    container
                    direction='column'
                    spacing={0}
                    wrap='nowrap'
                >
                    <TransactionHistoryAgGrid
                        setSelectedTransactionId={setSelectedTransactionId}
                        tableOptions={TableOptions}
                    />
                </Grid>
                <Grid
                    className={classes.pageSelectorContainer}
                    container
                    direction='row'
                    justifyContent='space-between'
                    spacing={0}
                    wrap='nowrap'
                >
                    <PageSelector
                        changePageOnApplyOnly
                        currentPageNum={TableOptions.PageNo}
                        onPageChange={setPageNum}
                        totalRows={rowCount}
                    />
                    {theme?.app?.clientTheme.layoutSettings.useTassatPayLogoOnTransactionHistory &&
                        <img
                            className={classes.tassatPayLogo}
                            src={TassatPayLogo}
                        />}
                </Grid>
            </Grid>
            <TransactionDetailsModal
                isLookup={Boolean(props.location.state?.linkedRequestId)}
                onClose={() => setSelectedTransactionId('')}
                open={Boolean(selectedTransactionId)}
                selectedTransactionId={selectedTransactionId}
            />
        </TableOptionsContext.Provider>
    );
}